import React, { createContext, useContext, useState, useEffect } from 'react';
import { CompanySettings } from '../types';
import { adminApi } from '../api/admin';
import { useAuth } from './AuthContext';

interface ThemeContextType {
  settings: CompanySettings | null;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
}

const defaultSettings: CompanySettings = {
  id: '',
  company_id: '',
  primary_color: '#1B2A4A',
  secondary_color: '#2D5F8A',
  accent_color: '#4ECDC4',
  app_name: 'JULIA',
  font_family: 'Inter',
  survey_access_mode: 'link',
  notification_preferences: {
    email_enabled: true,
    deadline_reminder_days: 7,
    audit_alerts: true,
    survey_responses: true,
  },
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function applyTheme(settings: CompanySettings) {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', settings.primary_color);
  root.style.setProperty('--color-secondary', settings.secondary_color);
  root.style.setProperty('--color-accent', settings.accent_color);
  if (settings.font_family) {
    root.style.setProperty('--font-family', `'${settings.font_family}', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`);
  }
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState<CompanySettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(false);

  const refreshSettings = async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.getSettings();
      const d = (response.data as any).settings || response.data;
      setSettings(d);
      applyTheme(d);
    } catch {
      applyTheme(defaultSettings);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      refreshSettings();
    } else {
      applyTheme(defaultSettings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return (
    <ThemeContext.Provider value={{ settings, isLoading, refreshSettings }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};
